import { RefObject, useCallback, useEffect } from 'react';

/**
 * Automatically moves focus to next referenced input when current one's number of chars is equal to max-length.
 * @example useAutoFocusNextInput([ref1, ref2, ref3]);
 * @param refs - array of references to relevant input elements
 */
const useAutoFocusNextInput = (refs: RefObject<HTMLInputElement>[]) => {
  const wrapListener = useCallback((nextElement: RefObject<HTMLInputElement>) => {
    return (ev: Event) => {
      const targetElement = ev.target as HTMLInputElement;
      if (
        (ev as InputEvent).inputType === 'insertText' &&
        targetElement.value.length === targetElement.maxLength
      ) {
        nextElement?.current?.focus();
        nextElement?.current?.select();
      }
    };
  }, []);

  useEffect(() => {
    // Save current state of ref array to ensure it is the same during cleanup.
    const els = refs.flatMap(ref => ref.current ?? []);
    const callbacks: ((ev: Event) => void)[] = [];

    els.forEach((el: HTMLInputElement, index: number) => {
      callbacks.push(wrapListener(refs[index + 1]));
      el.addEventListener('input', callbacks[index]);
    });

    return () => {
      els.forEach((el: HTMLInputElement, index: number) => {
        el.removeEventListener('input', callbacks[index]);
      });
    };
  }, refs);
};

export default useAutoFocusNextInput;
